import React, { useEffect, useMemo, useState } from 'react';
import {
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    Tab,
    Tabs,
    Typography,
    useMediaQuery,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import PhoneIcon from '@material-ui/icons/Phone';
import EmailIcon from '@material-ui/icons/Email';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import BusinessIcon from '@material-ui/icons/Business';
import ShoppingBasketIcon from '@material-ui/icons/ShoppingBasket';

import KlientOrdersInfo from '../../views/order/KlientOrdersInfo';
import { setDisplayListOrder } from '../../redux/TMPOrderListFastFood';
import { NumberFormatPrice } from '../NumberFormat/NumberFormat';
import returnPhoneNumberFormat from '../PhoneNumberFormat/returnPhoneNumberFormat';
import ClientAnalyticsChartApex from './ClientAnalyticsChartApex';

const useStyles = makeStyles((theme) => ({
    dialogTitle: {
        paddingBottom: 8,
    },
    nameRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        flexWrap: 'wrap',
    },
    statCard: {
        height: '100%',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 10,
        boxShadow: 'none',
    },
    profileCard: {
        height: '100%',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 10,
        boxShadow: 'none',
    },
    metricsGrid: {
        marginBottom: theme.spacing(2),
    },
    infoRow: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: theme.spacing(1.25),
        gap: theme.spacing(1),
    },
    infoIcon: {
        color: theme.palette.text.secondary,
        fontSize: 18,
    },
    chartWrap: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 10,
        padding: theme.spacing(1),
        minHeight: 320,
    },
    productRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: `${theme.spacing(1)}px ${theme.spacing(1.5)}px`,
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    productRowLast: {
        borderBottom: 'none',
    },
    emptyBox: {
        minHeight: 180,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: theme.palette.text.secondary,
    },
}));

const toNumber = (value) => {
    if (value === null || value === undefined) return 0;
    const normalized = String(value).replace(/\s/g, '').replace(',', '.');
    const numeric = Number(normalized);
    return Number.isFinite(numeric) ? numeric : 0;
};

const toDate = (order) => {
    if (order?.date_delivered) {
        const raw = toNumber(order.date_delivered);
        if (raw > 0) {
            // Поддерживаем оба формата: unix seconds и unix milliseconds.
            const timestampMs = raw > 1e12 ? raw : raw * 1000;
            const date = new Date(timestampMs);
            if (!Number.isNaN(date.getTime())) return date;
        }
    }

    const directDate =
        order?.dateCreateText ||
        order?.delivered?.dateDeliveredText ||
        order?.delivered?.dateDelivered ||
        order?.delivered?.dateSetOrder;

    if (directDate) {
        const date = new Date(directDate);
        if (!Number.isNaN(date.getTime())) return date;
    }

    return null;
};

const getOrders = (idKlient) => {
    const source = idKlient?.orderListKlient;
    if (!source) return [];

    if (Array.isArray(source)) return source;
    if (Array.isArray(source?.OrderList)) return source.OrderList;
    if (source?.OrderList && typeof source.OrderList === 'object') {
        return setDisplayListOrder(source.OrderList, 'all');
    }
    if (typeof source === 'object') {
        return setDisplayListOrder(source, 'all');
    }

    return [];
};

const buildStats = (orders = []) => {
    const monthOrders = new Array(12).fill(0);
    const monthTurnover = new Array(12).fill(0);
    const monthAvgCheck = new Array(12).fill(0);
    const productMap = {};

    let total = 0;
    let sum = 0;
    let sumNoDiscount = 0;

    orders.forEach((order) => {
        total += 1;

        const orderSum = toNumber(order?.ItogSumOrder);
        const orderNoDiscount = toNumber(order?.ItogSumOrderNoDiscount);
        sum += orderSum;
        sumNoDiscount += orderNoDiscount > 0 ? orderNoDiscount : orderSum;

        const date = toDate(order);
        if (date) {
            const month = date.getMonth();
            monthOrders[month] += 1;
            monthTurnover[month] += orderSum;
        }

        (order?.products || []).forEach((product) => {
            const id = product?.id_product || product?.id;
            const key = String(id || product?.name || product?.title || '');
            if (!key) return;

            if (!productMap[key]) {
                productMap[key] = {
                    id: key,
                    name: product?.name || product?.title || 'Без названия',
                    units: 0,
                    frequency: 0,
                };
            }

            productMap[key].units += toNumber(product?.count) || 1;
            productMap[key].frequency += 1;
        });
    });

    for (let i = 0; i < 12; i += 1) {
        monthAvgCheck[i] = monthOrders[i] > 0 ? monthTurnover[i] / monthOrders[i] : 0;
    }

    const topProducts = Object.values(productMap)
        .sort((a, b) => b.frequency - a.frequency)
        .slice(0, 10)
        .map((item) => ({
            ...item,
            frequencyPercent: total > 0 ? Math.round((item.frequency / total) * 100) : 0,
        }));

    return {
        total,
        sum,
        sumNoDiscount,
        average: total > 0 ? sum / total : 0,
        monthOrders,
        monthTurnover,
        monthAvgCheck,
        topProducts,
    };
};

const ClientInfoLine = ({ icon, label, value }) => {
    const classes = useStyles();

    return (
        <div className={classes.infoRow}>
            {icon}
            <Typography variant="body2" color="textSecondary">
                {label}:
            </Typography>
            <Typography variant="body2">{value || '-'}</Typography>
        </div>
    );
};

const MetricCard = ({ title, value, caption }) => {
    const classes = useStyles();

    return (
        <Card className={classes.statCard}>
            <CardContent>
                <Typography variant="body2" color="textSecondary">
                    {title}
                </Typography>
                <Typography variant="h5" style={{ marginTop: 6, fontWeight: 600 }}>
                    {value}
                </Typography>
                {caption ? (
                    <Typography variant="caption" color="textSecondary">
                        {caption}
                    </Typography>
                ) : null}
            </CardContent>
        </Card>
    );
};

const KlientInfoDialog = ({
    open2,
    onConfirmDialogClose,
    text,
    idKlient = {},
    title = 'Карточка клиента',
    noButton = 'Закрыть',
    updateKlientInfoCard,
    priorityTab = false,
}) => {
    const theme = useTheme();
    const classes = useStyles();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

    const defaultTab = typeof priorityTab === 'number' ? priorityTab : 0;
    const [tabIndex, setTabIndex] = useState(defaultTab);

    useEffect(() => {
        setTabIndex(typeof priorityTab === 'number' ? priorityTab : 0);
    }, [priorityTab, updateKlientInfoCard]);

    const klientInfo = idKlient?.klient_information || idKlient?.klient || {};
    const orders = useMemo(() => getOrders(idKlient), [idKlient]);
    const stats = useMemo(() => buildStats(orders), [orders]);

    const hasData = Boolean(idKlient && (idKlient?.klient_information || idKlient?.klient || orders.length));

    const fullName = [klientInfo?.firstName, klientInfo?.lastName].filter(Boolean).join(' ').trim() || 'Клиент без имени';
    const phone = klientInfo?.phone ? returnPhoneNumberFormat(String(klientInfo.phone)) : '-';

    return (
        <Dialog
            fullScreen={fullScreen}
            maxWidth="lg"
            fullWidth
            open={open2}
            onClose={onConfirmDialogClose}
            aria-labelledby="client-info-dialog-title"
        >
            <DialogTitle id="client-info-dialog-title" className={classes.dialogTitle}>
                <div className={classes.nameRow}>
                    <Avatar>
                        <PersonOutlineIcon />
                    </Avatar>
                    <Typography variant="h6">{fullName}</Typography>
                    <Chip size="small" color="primary" label={`Заказов: ${stats.total}`} />
                    <Chip size="small" variant="outlined" label={`Бонусы: ${toNumber(klientInfo?.bonus_points)}`} />
                </div>
                <Box mt={1}>
                    <Tabs
                        value={tabIndex}
                        onChange={(e, value) => setTabIndex(value)}
                        indicatorColor="primary"
                        textColor="primary"
                        variant="scrollable"
                        scrollButtons="auto"
                    >
                        <Tab label="Профиль" />
                        <Tab label="Предпочтения" />
                        <Tab label="История заказов" />
                    </Tabs>
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                {!hasData ? (
                    <div className={classes.emptyBox}>
                        <CircularProgress size={28} style={{ marginRight: 12 }} />
                        <Typography variant="body2">Загрузка карточки клиента...</Typography>
                    </div>
                ) : null}

                {hasData && tabIndex === 0 ? (
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                            <Card className={classes.profileCard}>
                                <CardContent>
                                    <Typography variant="subtitle1" style={{ marginBottom: 12, fontWeight: 600 }}>
                                        Профиль клиента
                                    </Typography>
                                    <ClientInfoLine icon={<PhoneIcon className={classes.infoIcon} />} label="Телефон" value={phone} />
                                    <ClientInfoLine icon={<EmailIcon className={classes.infoIcon} />} label="Email" value={klientInfo?.email || '-'} />
                                    <ClientInfoLine
                                        icon={<CalendarTodayIcon className={classes.infoIcon} />}
                                        label="День рождения"
                                        value={klientInfo?.birthdayText || '-'}
                                    />
                                    <ClientInfoLine
                                        icon={<BusinessIcon className={classes.infoIcon} />}
                                        label="Организация"
                                        value={klientInfo?.organizationName || klientInfo?.organization || '-'}
                                    />
                                    <ClientInfoLine
                                        icon={<ShoppingBasketIcon className={classes.infoIcon} />}
                                        label="Канал"
                                        value={klientInfo?.chanelName || klientInfo?.chanel || '-'}
                                    />
                                    <Divider style={{ margin: '12px 0' }} />
                                    <Typography variant="body2" color="textSecondary">
                                        Комментарий
                                    </Typography>
                                    <Typography variant="body2" style={{ marginTop: 6 }}>
                                        {klientInfo?.comment || 'Нет комментария'}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} md={8}>
                            <Grid container spacing={2} className={classes.metricsGrid}>
                                <Grid item xs={12} sm={6}>
                                    <MetricCard
                                        title="Общий оборот"
                                        value={`${NumberFormatPrice(stats.sum)} ₽`}
                                        caption="С учётом скидок"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <MetricCard
                                        title="Оборот без скидок"
                                        value={`${NumberFormatPrice(stats.sumNoDiscount)} ₽`}
                                        caption="Потенциальная выручка"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <MetricCard
                                        title="Средний чек"
                                        value={`${NumberFormatPrice(stats.average)} ₽`}
                                        caption="По всем заказам"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <MetricCard
                                        title="Количество заказов"
                                        value={NumberFormatPrice(stats.total)}
                                        caption="Всего за период"
                                    />
                                </Grid>
                            </Grid>

                            <div className={classes.chartWrap}>
                                <ClientAnalyticsChartApex
                                    height={320}
                                    monthOrders={stats.monthOrders}
                                    monthTurnover={stats.monthTurnover}
                                    monthAvgCheck={stats.monthAvgCheck}
                                />
                            </div>
                        </Grid>
                    </Grid>
                ) : null}

                {hasData && tabIndex === 1 ? (
                    <Card className={classes.profileCard}>
                        <CardContent>
                            <Typography variant="subtitle1" style={{ marginBottom: 12, fontWeight: 600 }}>
                                Топ-10 товаров клиента
                            </Typography>

                            {stats.topProducts.length === 0 ? (
                                <div className={classes.emptyBox}>
                                    <Typography variant="body2">Пока нет данных по покупкам</Typography>
                                </div>
                            ) : (
                                stats.topProducts.map((item, index) => (
                                    <div
                                        key={`top-product-${item.id}-${index}`}
                                        className={`${classes.productRow} ${
                                            index === stats.topProducts.length - 1 ? classes.productRowLast : ''
                                        }`}
                                    >
                                        <div>
                                            <Typography variant="body2" style={{ fontWeight: 500 }}>
                                                {item.name}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                Частота: {item.frequency} | Доля: {item.frequencyPercent}%
                                            </Typography>
                                        </div>
                                        <Chip size="small" label={`${NumberFormatPrice(item.units)} шт.`} />
                                    </div>
                                ))
                            )}
                        </CardContent>
                    </Card>
                ) : null}

                {hasData && tabIndex === 2 ? (
                    <KlientOrdersInfo
                        orderList={idKlient?.orderListKlient}
                        showType="all"
                        onRepeatOrderNavigate={onConfirmDialogClose}
                    />
                ) : null}

                {text ? (
                    <Box mt={2}>
                        <Typography variant="body2">{text}</Typography>
                    </Box>
                ) : null}
            </DialogContent>

            <DialogActions>
                <Button variant="outlined" color="secondary" onClick={onConfirmDialogClose}>
                    {noButton}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default KlientInfoDialog;
